import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { 
  Lock, 
  ArrowLeft, 
  Save, 
  Info, 
  MapPin, 
  Tag, 
  Type, 
  Banknote,
  FileText,
  Package,
  ShieldCheck,
  Image as ImageIcon
} from "lucide-react";
import { useModal } from "../context/ModalContext";

const apiUrl = process.env.REACT_APP_API_URL;

export default function EditPostPage() {
  const { showAlert } = useModal();
  const { id } = useParams();
  const navigate = useNavigate();
  const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const user = uStr ? JSON.parse(uStr) : null;

  const [form, setForm] = useState({
    title: "",
    price: "",
    condition: "",
    description: "",
    location: "",
    category_name: "",
  });

  const [meta, setMeta] = useState({});
  const [existingImages, setExistingImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    
    axios
      .get(`${apiUrl}/api/posts/${id}/edit`, {
        params: { userId: user.id },
      })
      .then((res) => {
        setForm(res.data.post);

        const metaObj = {};
        res.data.meta.forEach((m) => {
          metaObj[m.meta_key] = m.meta_value;
        });
        setMeta(metaObj);

        if (res.data.images) {
          setExistingImages(res.data.images);
        } else if (res.data.post.images) {
          setExistingImages(res.data.post.images);
        }

        setLoading(false);
      })
      .catch(() => {
        showAlert("Failed to load post", "error");
        setLoading(false);
      });
  }, [id, user?.id]);

  const update = async () => {
    if (updating) return;
    if (!form.title || !form.price) {
      showAlert("Title and price are required", "warning");
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`${apiUrl}/api/posts/${id}`, {
        ...form,
        meta,
      });

      showAlert("Post updated successfully!", "success");
      setTimeout(() => {
        navigate("/my-posts");
      }, 1500);
    } catch (err) {
      showAlert(err.response?.data?.error || "Failed to update post", "error");
    } finally {
      setUpdating(false);
    }
  };

  /* ============ READ-ONLY COMPONENT ============ */
  const ReadOnlyItem = ({ label, value, icon: Icon }) => (
    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 group transition-all">
      <div className="w-10 h-10 bg-white text-gray-400 rounded-xl flex items-center justify-center shadow-sm">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1">
          {label} <Lock size={8} />
        </p>
        <p className="font-bold text-gray-600 truncate max-w-[150px]">{value || "—"}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-20 flex justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-green-600/20 border-t-green-600 rounded-full animate-spin"></div>
            <p className="font-bold text-gray-400">Loading your post data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />


      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        {/* Navigation / Actions */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate("/my-posts")}
            className="flex items-center gap-2 text-gray-500 font-bold hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to My Posts
          </button>
          
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 rounded-xl text-xs font-bold border border-amber-100">
            <Info className="w-4 h-4" />
            Locked fields cannot be edited
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          
          {/* Main Form Section */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 p-8 md:p-10">
              <h1 className="text-3xl font-black text-gray-900 mb-8 tracking-tight">Edit Listing Details</h1>
              
              <div className="space-y-8">
                {/* Title */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 ml-1">
                    <Type className="w-4 h-4 text-green-600" /> Item Title
                  </label>
                  <input
                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-green-500 focus:bg-white outline-none transition font-medium text-lg"
                    placeholder="E.g. iPhone 15 Pro Max - Like New"
                    value={form.title}
                    onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                  />
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 ml-1">
                    <Banknote className="w-4 h-4 text-green-600" /> Asking Price (ETB)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-green-500 focus:bg-white outline-none transition font-bold text-xl text-green-600 pr-16"
                      placeholder="0.00"
                      value={form.price}
                      onChange={(e) => setForm((s) => ({ ...s, price: e.target.value }))}
                    />
                    <span className="absolute right-6 top-1/2 -translate-y-1/2 font-black text-gray-300">ETB</span>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 ml-1">
                    <MapPin className="w-4 h-4 text-green-600" /> Location / Pickup Point
                  </label>
                  <input
                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-green-500 focus:bg-white outline-none transition font-medium"
                    placeholder="E.g. Bole, Addis Ababa"
                    value={form.location || ""}
                    onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 ml-1">
                    <FileText className="w-4 h-4 text-green-600" /> Detailed Description
                  </label>
                  <textarea
                    rows="6"
                    className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-green-500 focus:bg-white outline-none transition font-medium resize-none"
                    placeholder="Tell buyers more about your item..."
                    value={form.description}
                    onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                  />
                </div>

                {/* Save Button */}
                <button
                  onClick={update}
                  disabled={updating}
                  className="w-full flex items-center justify-center gap-3 px-8 py-5 bg-green-600 text-white rounded-2xl font-black text-lg shadow-xl shadow-green-100 hover:bg-green-700 hover:shadow-green-200 transition-all disabled:opacity-50"
                >
                  {updating ? (
                    <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <Save className="w-6 h-6" />
                  )}
                  {updating ? "Saving Changes..." : "Update Post Now"}
                </button>
              </div>
            </div>

            {/* Image Preview Section */}
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-green-600" /> Current Photos
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {existingImages.map((img, i) => (
                  <div key={i} className="group relative aspect-square rounded-[1.5rem] overflow-hidden bg-gray-50 border-2 border-gray-100">
                    <img
                      src={typeof img === "string" ? img : img.url || img.image_url}
                      alt={`Post ${i}`}
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                    />
                    {i === 0 && (
                      <div className="absolute top-3 left-3 px-3 py-1 bg-green-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-lg">
                        Cover Photo
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Area: Non-Editable Data */}
          <div className="space-y-8">
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-600" /> Specifications
              </h3>
              
              <div className="space-y-4">
                <ReadOnlyItem label="Category" value={form.category_name} icon={Package} />
                <ReadOnlyItem label="Condition" value={form.condition} icon={ShieldCheck} />
                
                {Object.entries(meta).map(([k, v]) => (
                  <ReadOnlyItem 
                    key={k} 
                    label={k.replace(/_/g, " ")} 
                    value={v} 
                    icon={Tag} 
                  />
                ))}
              </div>
            </div>

            <div className="bg-green-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
              <h4 className="text-lg font-bold mb-2">Need help?</h4>
              <p className="text-sm text-green-100/70 mb-6 leading-relaxed">
                If you need to change the category or other locked fields, please contact our support team.
              </p>
              <button 
                onClick={() => navigate("/help")}
                className="w-full py-3 bg-white text-green-900 font-bold rounded-xl hover:bg-green-50 transition"
              >
                Go to Help Center
              </button>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
